import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, db } from "../utils/firebase";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
  reload,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  const syncVerifiedEmailState = async (firebaseUser = auth.currentUser) => {
    if (!firebaseUser?.emailVerified) return;

    try {
      const token = await firebaseUser.getIdToken();
      await fetch("/api/users/me/email-status", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    } catch {
      // Best-effort sync for welcome email queueing.
    }
  };

  const refreshCurrentUser = async (firebaseUser = auth.currentUser) => {
    if (!firebaseUser) return null;
    await reload(firebaseUser);
    setUser(auth.currentUser);
    return auth.currentUser;
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      const currentUser = firebaseUser ? await refreshCurrentUser(firebaseUser) : null;
      setUser(currentUser);
      if (currentUser) {
        try {
          const userRef = doc(db, "users", currentUser.uid);
          const userSnap = await getDoc(userRef);
          setRole(userSnap.exists() ? userSnap.data().role || "user" : "user");
        } catch {
          setRole("user");
        }
        await syncVerifiedEmailState(currentUser);
      } else {
        setRole(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const register = (email, password) =>
    createUserWithEmailAndPassword(auth, email, password);

  const sendVerificationEmail = (firebaseUser = auth.currentUser) => {
    if (!firebaseUser) {
      throw new Error("auth/no-current-user");
    }
    return sendEmailVerification(firebaseUser);
  };

  const login = async (email, password) => {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    const currentUser = await refreshCurrentUser(credential.user);

    if (!currentUser?.emailVerified) {
      await signOut(auth);
      const error = new Error("Please verify your email before signing in.");
      error.code = "auth/email-not-verified";
      throw error;
    }

    await syncVerifiedEmailState(currentUser);

    return credential;
  };

  const sendResetPasswordEmail = (email) =>
    sendPasswordResetEmail(auth, email);

  const logout = () => signOut(auth);

  const value = {
    user,
    role,
    login,
    register,
    logout,
    refreshCurrentUser,
    syncVerifiedEmailState,
    sendVerificationEmail,
    sendResetPasswordEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}